import React from 'react'
import {useInView} from 'react-intersection-observer'
import {
  DateTime,
  LocationLogEntry,
  RPCDocs,
  useCurrentUser,
} from '@therms/react-modules'

interface EntryListItemProps {
  entry: RPCDocs.Locations.GetLocationLogEntryList.v2.Entry
  onDownloadPDF?: (
    entry: RPCDocs.Locations.GetLocationLogEntryList.v2.Entry,
  ) => void
  onEditEntryId?: (entryId: string) => void
}

function EntryListItem({
  entry,
  onDownloadPDF,
  onEditEntryId,
}: EntryListItemProps) {
  const currentUser = useCurrentUser()

  const {inView, ref} = useInView({
    triggerOnce: true,
  })

  const canEdit = Boolean(
    onEditEntryId &&
    (currentUser.id === entry.createdByUser?.id
      ? currentUser.anp.role.ops.permissions.editLocationLogEntries
      : currentUser.anp.role.ops.permissions.editOtherUserLocationLogEntries),
  )

  // for performance reasons on large lists, we don't render the entire EntryCard because it contains the
  // FormBuilder renderer which is cpu intensive - this should allow smooth scrolling
  if (!inView) {
    return (
      <div key={entry.id} ref={ref}>
        <div className="bg-surface relative rounded">
          <div className="bg-surface-strong p-xs rounded-t">
            <div className="flex flex-col">
              <div className="inline-flex justify-between">
                <div className="font-bold">{entry.title}</div>

                <div>
                  <DateTime timestamp={entry.timestamp} />
                </div>
              </div>

              <div className="inline-flex justify-between">
                <div className="text-color-subtle">{entry.subTitle}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <LocationLogEntry
      entry={entry}
      dateMode="time"
      key={entry.id}
      onDownloadEntryPDF={
        onDownloadPDF ? () => onDownloadPDF(entry) : undefined
      }
      onEdit={canEdit ? () => onEditEntryId?.(entry.id) : undefined}
    />
  )
}

export {EntryListItem}
